import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useState, useEffect } from "react";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../../components/ButtonComponent";
import SelectComponent from "../../../components/SelectComponent";
import Toast from "../../../helpers/Toast";
import { AssistanceCheckListService } from "../../../services/AssistanceCheckListService";
import { AssistanceDTO } from "../../../models/Assistance/AssistanceDTO";
import { Parameter } from "../../../models/Parameter";
import { EditAssistanceCheckListDTO } from "../../../models/AssistanceCheckList/EditAssistanceCheckListDTO";
import { useIntl } from "react-intl";


type SelectItemsProps = {
  value: string;
  label: string;
};

interface Props {
  assistance?: AssistanceDTO;
  currentChecklistId: number | null;
  currentChecklistTitle: string | null;
  loading: boolean;
  setAssistance?: (a: AssistanceDTO) => void;
  userCanEdit?: boolean;
}

export default function SwitchCheckListComponent({
  assistance,
  currentChecklistId,
  currentChecklistTitle,
  loading,
  setAssistance,
  userCanEdit,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditButtonLoading, setIsEditButtonLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>(currentChecklistId?.toString() ?? '');
  const [checklistOptions, setChecklistOptions] = useState<SelectItemsProps[]>([]);
  const intl = useIntl();
  const service = new AssistanceCheckListService();

  // Sincroniza o selectedId quando currentChecklistId mudar
  useEffect(() => {
    setSelectedId(currentChecklistId?.toString() ?? '');
  }, [currentChecklistId]);

  // Carrega as opções de checklist baseado na assistência
  useEffect(() => {
    const fetchOptions = async () => {
      const params: Parameter[] = [];
      if (assistance?.equipment?.model) params.push(new Parameter('model', assistance.equipment.model));
      if (assistance?.equipment?.brand) params.push(new Parameter('brand', assistance.equipment.brand));

      const resp = await service.GetAll(params, [], 1, 100);
      if (resp.success && resp.obj) {
        setChecklistOptions(
          resp.obj.map((m: any) => ({ value: m.id.toString(), label: m.title }))
        );
      } else {
        Toast.Show('error', resp.message || 'Erro ao obter modelos de checklist.');
        setChecklistOptions([]);
      }
    };
    fetchOptions();
  }, [assistance]);

  const editAssistanceCheckListData = async () => {
    if (!assistance?.assistanceCheckList?.id) return;

    setIsEditButtonLoading(true);

    const dto: EditAssistanceCheckListDTO = {
      id: assistance.assistanceCheckList.id,
      checkListModelId: selectedId !== '' ? Number(selectedId) : null,
    };

    const respEdit = await service.Edit(dto);
    setIsEditButtonLoading(false);

    if (!respEdit.success) {
      Toast.Show('error', respEdit.message);
      return;
    }

    Toast.Show('success', respEdit.message);

    // Busca o checklist atualizado no backend para garantir dados completos
    const respGet = await service.GetById(assistance.assistanceCheckList.id);
    if (!respGet.success) {
      Toast.Show('error', 'Erro ao buscar checklist atualizado.');
      return;
    }

    // Atualiza o estado da assistência com o checklist completo atualizado
    if (setAssistance && assistance) {
      setAssistance({
        ...assistance,
        assistanceCheckList: respGet.obj,
      });
    }

    setIsOpen(false);
  };

  return (
    <>
      <Col md={4} sm={4} xs={4} lg={4} style={{ marginBottom: 8 }}>
        <ButtonComponent
          onClick={() => setIsOpen(true)}
          style={{ width: '100%' }}
          text={intl.formatMessage({ id: 'Screens.AssistanceCheckListModel.Edit.Title' })}
          disabled={!userCanEdit || loading}
        />
      </Col>

      <Dialog open={isOpen} fullWidth maxWidth="sm">
        <DialogTitle>
          {intl.formatMessage({ id: 'Screens.AssistanceCheckListModel.Edit.Title' })}
        </DialogTitle>
        <DialogContent>
          <Row style={{ margin: 0, padding: 0 }}>
            <Col md={6} sm={12} xs={12}>
              <SelectComponent
                label={intl.formatMessage({ id: 'Screens.AssistanceCheckListModel.Edit.Name' })}
                selectedItemValue={currentChecklistId?.toString() ?? ''}
                items={[
                  {
                    value: currentChecklistId?.toString() ?? '',
                    label: currentChecklistTitle ?? intl.formatMessage({ id: 'Common.Labels.NoModel' }),
                  },
                ]}
                onChange={() => { }}
                blockItem
              />
            </Col>
            <Col md={6} sm={12} xs={12}>
              <SelectComponent
                label={intl.formatMessage({ id: 'Screens.AssistanceCheckListModel.Edit.NewName' })}
                selectedItemValue={selectedId}
                items={checklistOptions}
                onChange={setSelectedId}
              />
            </Col>
          </Row>
        </DialogContent>
        <DialogActions>
          <Col md={6} sm={6} xs={6} lg={6}>
            <ButtonComponent
              onClick={editAssistanceCheckListData}
              style={{ width: '97%' }}
              text={intl.formatMessage({ id: 'Common.Buttons.Save' })}
              loading={isEditButtonLoading}
              disabled={false}
            />
          </Col>
          <Col md={6} sm={6} xs={6} lg={6}>
            <ButtonComponent
              onClick={() => setIsOpen(false)}
              style={{ width: '97%' }}
              text={intl.formatMessage({ id: 'Common.Buttons.Cancel' })}
            />
          </Col>
        </DialogActions>
      </Dialog>
    </>
  );
}